import * as React from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Typography,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    CircularProgress,
    Tooltip,
    Paper,
    TextField,
    FormControl,
    Select,
    InputLabel,
    IconButton,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { makeApiRequest } from '../../../utils/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import ViewLead from './ViewLead';
const formatDateToDisplay = (date) => {
    if (!date) return '';
    
    const [year, month, day] = date.split('-');
    
    const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    const monthIndex = parseInt(month, 10) - 1;
    const monthName = monthNames[monthIndex];
    
    const dayNum = parseInt(day, 10);
    
    return `${dayNum} ${monthName} ${year}`;
};

const SearchLead = () => {
    const [searchData, setSearchData] = React.useState({
        name: '',
        phone: '',
        createdDate: '',
        status: '',
        followUpDateFrom: '',
        followUpDateTo: '',
    });
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [loading, setLoading] = React.useState(false);
    const [filteredLeads, setFilteredLeads] = React.useState([]);
    const [totalLeads, setTotalLeads] = React.useState(0);
    const [error, setError] = React.useState('');
    const [apiCallMade, setApiCallMade] = React.useState(false);
    const [dateErrors, setDateErrors] = React.useState({
        followUpDateFrom: '',
        followUpDateTo: '',
    });
    const [selectedLead, setSelectedLead] = React.useState(null); // State for selected lead
    const [openDialog, setOpenDialog] = React.useState(false); // State for dialog

    const placeholderColor = '#9E9E9E';

    const statusOptions = [
        { value: 'New', label: 'New' },
        { value: 'In-progress', label: 'In-progress' },
        { value: 'Deferred', label: 'Deferred' },
        { value: 'Lost', label: 'Lost' },
        { value: 'Converted', label: 'Converted' },
        { value: 'Overdue', label: 'Overdue' },
    ];

    const handleChange = (field) => (event) => {
        const value = event.target.value;
        setSearchData(prevData => ({
            ...prevData,
            [field]: value,
        }));
        setDateErrors(prev => ({
            ...prev,
            [field]: '',
        }));
    };

    const fetchLeads = async (currentPage, perPage) => {
        setLoading(true);
        setError('');
        setApiCallMade(true);

        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('Please log in to search leads');
            }

            const requestData = {
                token,
                name: searchData.name || null,
                phone: searchData.phone || null,
                created_date: searchData.createdDate || null,
                status: searchData.status || null,
                follow_up_date_from: searchData.followUpDateFrom || null,
                follow_up_date_to: searchData.followUpDateTo || null,
                page: currentPage + 1,
                per_page: perPage,
            };

            const response = await makeApiRequest('POST', '/api/leads/search', requestData);
            const { data } = response;
            setFilteredLeads(data.leads);
            setTotalLeads(data.pagination.total);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch leads');
            toast.error(err.response?.data?.message || 'Failed to fetch leads');
            setFilteredLeads([]);
            setTotalLeads(0);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllFilteredLeads = async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                throw new Error('Please log in to search leads');
            }

            const requestData = {
                token,
                name: searchData.name || null,
                phone: searchData.phone || null,
                created_date: searchData.createdDate || null,
                status: searchData.status || null,
                follow_up_date_from: searchData.followUpDateFrom || null,
                follow_up_date_to: searchData.followUpDateTo || null,
            };

            const response = await makeApiRequest('POST', '/api/leads/search/all', requestData);
            if (response && response.status === 'success') {
                return response.data.leads;
            } else {
                toast.error(response?.message || 'Failed to fetch leads for export');
                return [];
            }
        } catch (error) {
            console.error('Export fetch error:', error);
            toast.error('Error fetching leads for export');
            return [];
        }
    };

    const handleExportExcel = async () => {
        const leads = await fetchAllFilteredLeads();
        if (leads.length === 0) return;

        const exportData = leads.map((lead, index) => ({
            'S.No': index + 1,
            Name: lead.name,
            Email: lead.email || '—',
            Phone: lead.phone || '—',
            'Follow-Up Date': formatDateToDisplay(lead.next_follow_up_date) || '—',
            'Course & Department': `${lead.course || '—'} / ${lead.department || '—'}`,
            'Passed-out Year': lead.pass_out_year || '—',
            'Created By & Date': `${lead.created_by || '—'} on ${formatDateToDisplay(lead.created_date) || '—'}`,
            Status: lead.status || '—',
            Remark: lead.remarks || '—'
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Leads');
        XLSX.writeFile(wb, 'leads_export.xlsx');
    };

    const handleExportPDF = async () => {
        const leads = await fetchAllFilteredLeads();
        if (leads.length === 0) return;

        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
        });
        doc.setFontSize(16);
        doc.text('Leads Report', 14, 20);

        const tableColumn = [
            'S.No', 'Name', 'Email', 'Phone', 'Follow-Up Date',
            'Course & Dept', 'Year', 'Created By & Date', 'Status', 'Remark'
        ];

        const tableRows = leads.map((lead, index) => [
            index + 1,
            lead.name,
            lead.email || '—',
            lead.phone || '—',
            formatDateToDisplay(lead.next_follow_up_date) || '—',
            `${lead.course || '—'} / ${lead.department || '—'}`,
            lead.pass_out_year || '—',
            `${lead.created_by || '—'} on ${formatDateToDisplay(lead.created_date) || '—'}`,
            lead.status || '—',
            lead.remarks || '—'
        ]);

        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 30,
            margin: { left: 10, right: 10 },
            styles: {
                fontSize: 8,
                cellPadding: 1.5,
                overflow: 'linebreak',
            },
            columnStyles: {
                0: { cellWidth: 8 },
                1: { cellWidth: 20 },
                2: { cellWidth: 25 },
                3: { cellWidth: 15 },
                4: { cellWidth: 20 },
                5: { cellWidth: 20 },
                6: { cellWidth: 12 },
                7: { cellWidth: 20 },
                8: { cellWidth: 15 },
                9: { cellWidth: 15 },
            },
            theme: 'striped',
            headStyles: {
                fillColor: [200, 200, 200],
                textColor: [0, 0, 0],
                fontSize: 8,
            },
            bodyStyles: {
                textColor: [0, 0, 0],
            },
        });

        doc.save('leads_export.pdf');
    };

    const handleSearch = () => {
        if (searchData.followUpDateTo && !searchData.followUpDateFrom) {
            setDateErrors(prev => ({
                ...prev,
                followUpDateFrom: 'Please select Follow-up From date',
            }));
            return;
        }
        if (searchData.followUpDateFrom && !searchData.followUpDateTo) {
            setDateErrors(prev => ({
                ...prev,
                followUpDateTo: 'Please select Follow-up To date',
            }));
            return;
        }

        setPage(0);
        fetchLeads(0, rowsPerPage);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        fetchLeads(newPage, rowsPerPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        fetchLeads(0, newRowsPerPage);
    };

    const handleViewLead = (lead) => {
        setSelectedLead(lead);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedLead(null);
    };

    const columns = [
        {
            id: 'sno',
            label: 'S.NO',
            minWidth: 80,
            format: (value, index) => page * rowsPerPage + index + 1
        },
        {
            id: 'name',
            label: 'Name',
            minWidth: 200,
            format: (value, row) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton 
                        onClick={() => handleViewLead(row)} 
                        size="small"
                        sx={{ mr: 1 }}
                    >
                        <VisibilityIcon sx={{ color: '#1976d2' }} />
                    </IconButton>
                    <Tooltip
                        title={value}
                        placement="top"
                        sx={{
                            fontSize: '2rem',
                            padding: '16px 24px',
                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                            maxWidth: '800px',
                        }}
                    >
                        <span>{value}</span>
                    </Tooltip>
                </Box>
            )
        },
        {
            id: 'email',
            label: 'Email',
            minWidth: 200,
            format: (value) => value ? (
                <Tooltip
                    title={value}
                    placement="top"
                    sx={{
                        fontSize: '2rem',
                        padding: '16px 24px',
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        maxWidth: '800px',
                    }}
                >
                    <a
                        href={`https://mail.google.com/mail/?view=cm&fs=1&to=${value}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                        {value}
                    </a>
                </Tooltip>
            ) : '—'
        },
        {
            id: 'phone',
            label: 'Phone',
            minWidth: 150,
            align: 'center',
            format: (value) => (
                <span style={{ color: 'green' }}>
                    {value && value.length === 10
                        ? `${value.slice(0, 5)} ${value.slice(5)}`
                        : value || '—'}
                </span>
            )
        },
        {
            id: 'next_follow_up_date',
            label: 'Follow-Up Date',
            minWidth: 150,
            format: (value) => (
                <span style={{ color: 'purple' }}>
                    {value ? formatDateToDisplay(value) : '—'}
                </span>
            )
        },
        {
            id: 'course_department',
            label: 'Course & Department',
            minWidth: 200,
            format: (value, row) => (
                <Tooltip
                    title={`${row.department || '—'} / ${row.course || '—'}`}
                    placement="top"
                    sx={{
                        fontSize: '2rem',
                        padding: '16px 24px',
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        maxWidth: '800px',
                    }}
                >
                    <span>
                        {!row.department && !row.course ? (
                            '—'
                        ) : (
                            <>
                                {row.department || '—'}
                                <br />
                                <span style={{ color: '#757575' }}>{row.course || '—'}</span>
                            </>
                        )}
                    </span>
                </Tooltip>
            )
        },
        {
            id: 'pass_out_year',
            label: 'Passed-out Year',
            minWidth: 120,
            format: (value) => value || '—'
        },
        {
            id: 'created_info',
            label: 'Created By & Date',
            minWidth: 200,
            format: (value, row) => (
                <Tooltip
                    title={`${row.created_by || '—'} on ${row.created_date ? formatDateToDisplay(row.created_date) : '—'}`}
                    placement="top"
                    sx={{
                        fontSize: '2rem',
                        padding: '16px 24px',
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        maxWidth: '800px',
                    }}
                >
                    <span>
                        {row.created_by || '—'}
                        <br />
                        <span style={{ color: '#757575', fontSize: '12px' }}>
                            on {row.created_date ? formatDateToDisplay(row.created_date) : '—'}
                        </span>
                    </span>
                </Tooltip>
            )
        },
        {
            id: 'remarks',
            label: 'Remark',
            minWidth: 150,
            width: 'auto',
            format: (value) => (
                <Tooltip
                    title={value || '—'}
                    placement="top"
                    sx={{
                        fontSize: '2rem',
                        padding: '16px 24px',
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        maxWidth: '800px',
                    }}
                >
                    <span>{value || '—'}</span>
                </Tooltip>
            )
        }
    ];

    const commonInputSx = {
        backgroundColor: '#fff',
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#e0e0e0',
            },
            '&:hover fieldset': {
                borderColor: '#1976d2',
            },
            height: '56px',
        },
        '& .MuiInputBase-input::placeholder': {
            color: placeholderColor,
            fontSize: '1rem',
            opacity: 1,
        },
        '& input::-webkit-input-placeholder': { 
            color: placeholderColor,
            opacity: 1,
        },
        '& input::-moz-placeholder': { 
            color: placeholderColor,
            opacity: 1,
        },
        '& input:-ms-input-placeholder': { 
            color: placeholderColor,
            opacity: 1,
        },
    };

    const dateInputSx = {
        ...commonInputSx,
        '& .MuiInputBase-input': {
            color: (theme) => (value) => value ? '#000' : placeholderColor,
        },
    };

    return (
        <Box
            sx={{
                backgroundColor: '#f5f7fa',
                padding: '20px',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                pt: 4,
            }}
        >
            <ToastContainer
                position="top-center"
                autoClose={2000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick={false}
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover={false}
                theme="light"
            />
            <Box sx={{ height: '80px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mb: 2 }}>
                <Typography variant="h4" sx={{ color: '#481E82', fontWeight: '500' }}>
                    Search Lead
                </Typography>
                <Box>
                    <Button
                        variant="contained"
                        onClick={handleExportPDF}
                        startIcon={<PictureAsPdfIcon />}
                        disabled={!filteredLeads.length}
                        sx={{
                            backgroundColor: "orange",
                            width: { xs: "100%", sm: "fit-content" },
                            marginRight: { xs: 0, sm: 2 }
                        }}
                    >
                        Download as PDF
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleExportExcel}
                        startIcon={<GridOnIcon />}
                        disabled={!filteredLeads.length}
                        sx={{
                            backgroundColor: "#4CAF50",
                            "&:hover": { backgroundColor: "#45A049" },
                            width: { xs: "100%", sm: "fit-content" },
                            marginTop: { xs: 1, sm: 0 }
                        }}
                    >
                        Export to EXCEL
                    </Button>
                </Box>
            </Box>
            <Card
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
                    borderRadius: '8px',
                    padding: '16px 0',
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                id="outlined-name"
                                label="Name"
                                variant="outlined"
                                fullWidth
                                size="small"
                                value={searchData.name}
                                onChange={handleChange('name')}
                                placeholder="Enter Name"
                                InputLabelProps={{ shrink: true }}
                                sx={commonInputSx}
                                inputProps={{
                                    style: {
                                        color: searchData.name ? '#000' : placeholderColor
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                id="outlined-phone"
                                label="Phone"
                                variant="outlined"
                                fullWidth
                                size="small"
                                value={searchData.phone}
                                onChange={handleChange('phone')}
                                placeholder="Enter Phone Number"
                                InputLabelProps={{ shrink: true }}
                                sx={commonInputSx}
                                inputProps={{
                                    style: {
                                        color: searchData.phone ? '#000' : placeholderColor
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                id="outlined-created-date"
                                label="Created Date"
                                variant="outlined"
                                fullWidth
                                type="date"
                                size="small"
                                value={searchData.createdDate}
                                onChange={handleChange('createdDate')}
                                placeholder="mm-dd-yyyy"
                                InputLabelProps={{ shrink: true }}
                                sx={dateInputSx}
                                inputProps={{
                                    style: {
                                        color: searchData.createdDate ? '#000' : placeholderColor
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl
                                size="medium"
                                variant="outlined"
                                fullWidth
                                sx={{
                                    backgroundColor: '#fff',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#e0e0e0',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#1976d2',
                                        },
                                        height: '56px',
                                    },
                                }}
                            >
                                <InputLabel
                                    id="status-select-label"
                                    sx={{ fontSize: '1rem' }}
                                    shrink
                                >
                                    Status
                                </InputLabel>
                                <Select
                                    labelId="status-select-label"
                                    id="status-select"
                                    value={searchData.status}
                                    label="Status"
                                    onChange={handleChange('status')}
                                    displayEmpty
                                    renderValue={(selected) => {
                                        if (!selected) {
                                            return <span style={{ color: placeholderColor }}>Select Status</span>;
                                        }
                                        return selected;
                                    }}
                                    sx={{
                                        '& .MuiSelect-select': {
                                            fontSize: '1rem',
                                            color: searchData.status ? '#000' : placeholderColor,
                                        },
                                    }}
                                >
                                    <MenuItem value="">
                                        <span style={{ color: placeholderColor }}>Select Status</span>
                                    </MenuItem>
                                    {statusOptions.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                            sx={{ fontSize: '1rem' }}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                id="outlined-follow-up-from"
                                label="Follow-Up Date From"
                                variant="outlined"
                                fullWidth
                                type="date"
                                size="small"
                                value={searchData.followUpDateFrom}
                                onChange={handleChange('followUpDateFrom')}
                                placeholder="mm-dd-yyyy"
                                InputLabelProps={{ shrink: true }}
                                sx={dateInputSx}
                                inputProps={{
                                    style: {
                                        color: searchData.followUpDateFrom ? '#000' : placeholderColor
                                    }
                                }}
                                error={!!dateErrors.followUpDateFrom}
                                helperText={dateErrors.followUpDateFrom}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                id="outlined-follow-up-to"
                                label="Follow-Up Date To"
                                variant="outlined"
                                fullWidth
                                type="date"
                                size="small"
                                value={searchData.followUpDateTo}
                                onChange={handleChange('followUpDateTo')}
                                placeholder="mm-dd-yyyy"
                                InputLabelProps={{ shrink: true }}
                                sx={dateInputSx}
                                inputProps={{
                                    style: {
                                        color: searchData.followUpDateTo ? '#000' : placeholderColor
                                    }
                                }}
                                error={!!dateErrors.followUpDateTo}
                                helperText={dateErrors.followUpDateTo}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSearch}
                                sx={{
                                    backgroundColor: '#1976d2',
                                    '&:hover': {
                                        backgroundColor: '#1565c0',
                                    },
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                    minWidth: '150px',
                                    height: '56px',
                                }}
                            >
                                Search
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {error && (
                <Typography color="error" sx={{ mt: 2 }}>
                    {error}
                </Typography>
            )}

            {apiCallMade && !loading && !error && filteredLeads.length === 0 && (
                <Box sx={{ mt: 2, p: 3, width: '100%', textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                        No leads found matching your search criteria.
                    </Typography>
                </Box>
            )}

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" width="100%" minHeight="400px">
                    <CircularProgress size={40} thickness={4} sx={{ color: '#481E82' }} />
                </Box>
            ) : filteredLeads.length > 0 && (
                <Paper sx={{ width: '100%', overflow: 'hidden', mt: 3, boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)' }}>
                    <TableContainer sx={{ maxHeight: '60vh', overflowX: 'auto' }}>
                        <Table aria-label="sticky table">
                            <TableHead sx={{ backgroundColor: '#faf0e6' }}>
                                <TableRow style={{backgroundColor: '#faf0e6'}}>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align={column.align}
                                            style={{
                                                minWidth: column.minWidth,
                                                width: column.width,
                                                fontWeight: 'bold',
                                                backgroundColor: '#ECEDFF',
                                            }}
                                        >
                                            {column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredLeads
                                    .map((row, index) => {
                                        return (
                                            <TableRow
                                                hover
                                                role="checkbox"
                                                tabIndex={-1}
                                                key={row.id || index}
                                                sx={{
                                                    padding: '8px',
                                                    '&:nth-of-type(even)': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                    },
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                    },
                                                }}
                                            >
                                                {columns.map((column) => {
                                                    let value;

                                                    if (column.id === 'sno') {
                                                        value = column.format(null, index);
                                                    } else if (column.id === 'course_department' || column.id === 'created_info') {
                                                        value = column.format(null, row);
                                                    } else {
                                                        value = row[column.id];
                                                        if (column.format && value !== undefined) {
                                                            value = column.format(value, row);
                                                        }
                                                    }

                                                    return (
                                                        <TableCell
                                                            key={column.id}
                                                            align={column.align}
                                                            sx={{
                                                                padding: '8px 16px',
                                                                borderBottom: '1px solid #e0e0e0',
                                                            }}
                                                        >
                                                            {value}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 100]}
                        component="div"
                        count={totalLeads}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                margin: '0',
                            },
                        }}
                    />
                </Paper>
            )}

            {selectedLead && (
                <ViewLead lead={selectedLead} onClose={handleCloseDialog} />
            )}
        </Box>
    );
};

export default SearchLead;